/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Information } from '@carbon/react/icons';
import {
  Button,
  InlineLoading,
  ListItem,
  TextArea,
  TextAreaSkeleton,
  TooltipIcon,
  UnorderedList
} from 'carbon-components-react';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { ApiStatus, LoaderStates } from '@ibm-watson/shared/ts';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { disableSaveCreateButton } from '../../../store/channels/actions';
import { connectServiceDeskIntegration } from '../../../store/service-desks/actions';
import {
  getConnectServiceDeskStatus,
  isLoadServiceDeskLoading,
  selectDraftServiceDesk,
  selectShouldSaveServiceDesk
} from '../../../store/service-desks/selectors';
import { links } from '../links';
import {
  Salesforce as messages,
  ServiceDesk as commonMessages
} from '../messages';
import {
  encodeSalesforceSettings,
  getSfDomain,
  getDeploymentId,
  getOrganizationId,
  getButtonId
} from './helpers';
import { FIELDS } from './schema';

import './Connect.scss';

const IDS = {
  CONNECT_ACCOUNT_BUTTON: 'salesforce_connect_account'
};

interface SalesforceConnectProps {
  /**
   *
   */
  assistantId: string;
  /**
   *
   */
  environmentId: string;
  /**
   *
   */
  formMethods: any;
  /**
   *
   */
  markStepComplete?: (complete: boolean) => void;
  /**
   *
   */
  readOnly?: boolean;
  /**
   *
   */
  updateServiceDeskId: (id: string) => void;
}
export const Connect = ({
  assistantId,
  environmentId,
  formMethods,
  updateServiceDeskId,
  markStepComplete,
  readOnly
}: SalesforceConnectProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const {
    getFieldValue,
    isValidAndPopulated,
    registerInputField,
    updateFieldValue
  } = useFormHelpers(formMethods);

  const { reset } = formMethods;

  const connectServiceDeskStatus = useAppSelector(getConnectServiceDeskStatus);
  const draftServiceDesk = useAppSelector(selectDraftServiceDesk, shallowEqual);
  const isServiceDeskLoading = useAppSelector(isLoadServiceDeskLoading);
  const shouldSaveServiceDesk = useAppSelector(selectShouldSaveServiceDesk);

  const isButtonCodeValidAndPopulated = isValidAndPopulated(FIELDS.BUTTON_CODE);
  const buttonCodeField = FIELDS.BUTTON_CODE;
  const buttonCode = getFieldValue(buttonCodeField);

  const isDeploymentCodeValidAndPopulated = isValidAndPopulated(
    FIELDS.ORGANIZATION_DEPLOYMENT_CODE
  );
  const deploymentCodeField = FIELDS.ORGANIZATION_DEPLOYMENT_CODE;
  const deploymentCode = getFieldValue(deploymentCodeField);

  const isConnectButtonDisabled =
    readOnly || // no write access
    (!isButtonCodeValidAndPopulated && !isDeploymentCodeValidAndPopulated) || // nothing has been changed
    !shouldSaveServiceDesk; // nothing new to connect or save

  const buttonCodeState = useState<boolean>(false);
  const deploymentCodeState = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string>('');

  /**
   * Connect the Salesforce integration and call update the service
   * desk ID inside the webchat's settings.
   */
  const onConnectAccount = useCallback(async () => {
    const adjustedServiceDesk = encodeSalesforceSettings(draftServiceDesk);

    if (
      _.isNil(
        _.get(adjustedServiceDesk, 'settings.private.secrets.subscription')
      )
    ) {
      _.set(adjustedServiceDesk, 'settings.private.secrets.subscription', {});
    }

    if (
      !_.isNil(
        _.get(
          adjustedServiceDesk,
          'settings.private.subscription.agent_app_url'
        )
      )
    ) {
      _.set(
        adjustedServiceDesk,
        'settings.private.subscription.agent_app_url',
        undefined
      );
    }

    const connectedIntegration = await dispatch(
      connectServiceDeskIntegration({
        assistantId,
        environmentId,
        integration: adjustedServiceDesk
      })
    ).unwrap();

    updateServiceDeskId(connectedIntegration?.integration_id);

    // Reset the form now that is has been saved.
    reset(_.cloneDeep(connectedIntegration.settings));
  }, [
    assistantId,
    dispatch,
    draftServiceDesk,
    environmentId,
    reset,
    updateServiceDeskId
  ]);

  /**
   * If the connect account button is enabled, then the user cannot
   * click on Save and Exit before connecting their Salesforce account.
   */
  useEffect(() => {
    dispatch(
      disableSaveCreateButton(
        !isConnectButtonDisabled ||
          !isDeploymentCodeValidAndPopulated ||
          !isButtonCodeValidAndPopulated
      )
    );
  }, [
    dispatch,
    isButtonCodeValidAndPopulated,
    isConnectButtonDisabled,
    isDeploymentCodeValidAndPopulated
  ]);

  /**
   * Determine when the Next button should be enabled using whether
   * or not the button and deployment codes are populated and dirty.
   */
  useEffect(() => {
    const isNextButtonEnabled =
      (connectServiceDeskStatus === ApiStatus.SUCCEEDED || // connect succeeded
        !shouldSaveServiceDesk) &&
      isButtonCodeValidAndPopulated &&
      isDeploymentCodeValidAndPopulated; // no changed required to save

    markStepComplete(isNextButtonEnabled);
  }, [
    connectServiceDeskStatus,
    getFieldValue,
    isButtonCodeValidAndPopulated,
    isDeploymentCodeValidAndPopulated,
    markStepComplete,
    shouldSaveServiceDesk
  ]);

  /**
   * When the button code is populated and/or changed,
   * parse the code for the button ID and save it into settings.
   */
  useEffect(() => {
    // The button code is usually encoded in the integration settings,
    // due to how scripts/code need to be safe before they're saved.
    // Decoding an encoded value is mandatory, and decoding an
    // already-decoded value does not mutate the value.
    const decodedButtonCode = decodeURIComponent(buttonCode);

    updateFieldValue(FIELDS.BUTTON_ID, getButtonId(decodedButtonCode) ?? '');

    updateFieldValue(FIELDS.BUTTON_CODE, decodedButtonCode);
    formMethods.trigger();
  }, [buttonCode, formMethods, updateFieldValue]);

  /**
   * When the deployment code is populated and/or changed,
   * parse the code for the account ID, organization ID, and domain,
   * then save it into settings.
   */
  useEffect(() => {
    // The button code is usually encoded in the integration settings,
    // due to how scripts/code need to be safe before they're saved.
    // Decoding an encoded value is mandatory, and decoding an
    // already-decoded value does not mutate the value.
    const decodedDeploymentCode = decodeURIComponent(deploymentCode);

    updateFieldValue(
      FIELDS.ACCOUNT_ID,
      getDeploymentId(decodedDeploymentCode) ?? ''
    );
    updateFieldValue(
      FIELDS.ORGANIZATION_ID,
      getOrganizationId(decodedDeploymentCode) ?? ''
    );
    updateFieldValue(
      FIELDS.SALESFORCE_DOMAIN,
      getSfDomain(decodedDeploymentCode) ?? ''
    );

    updateFieldValue(
      FIELDS.ORGANIZATION_DEPLOYMENT_CODE,
      decodedDeploymentCode
    );
    formMethods.trigger();
  }, [deploymentCode, formMethods, updateFieldValue]);

  /**
   * Show an error message depending on what the connect account
   * status is.
   */
  useEffect(() => {
    let message = null;
    switch (connectServiceDeskStatus) {
      case ApiStatus.LOADING:
        message = t(commonMessages.connecting);
        break;
      case ApiStatus.ERROR:
        message = t(commonMessages.notConnected);
        break;
      case ApiStatus.SUCCEEDED:
        message = t(commonMessages.connected);
        break;
      default:
        message = '';
    }
    setStatusMessage(message);
  }, [t, connectServiceDeskStatus]);

  return (
    <div>
      <div className="TabContent__field-container">
        <div className="TabContent__inner-title-heading__label">
          <h5 className="TabContent__inner-title TabContent__inner-title-heading bx--label">
            <FormattedMessage {...messages.beginningStepsTitle} />
          </h5>
          <TooltipIcon
            className="TabContent__inner-title-heading__tooltip"
            direction="bottom"
            tooltipText={messages.beginningStepsTooltip.defaultMessage}>
            <Information size={16} />
          </TooltipIcon>
        </div>
        <UnorderedList className="TabContent__unordered-list">
          <ListItem>
            <FormattedMessage {...messages.beginningStep1} />
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.beginningStep2} />
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.beginningStep3} />
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.beginningStep4} />
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.beginningStep5} />
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.beginningStep6} />
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.beginningStep7} />
          </ListItem>
        </UnorderedList>
      </div>
      <div className="ServiceDesk__divider"></div>
      <div className="TabContent__spacer16"></div>
      <div className="TabContent__field-container">
        <h5 className="TabContent__inner-title TabContent__inner-title-heading">
          <FormattedMessage {...messages.connectTitle} />
        </h5>
        <p>
          <FormattedMessage {...messages.connectDescription1} />
        </p>
        <div className="TabContent__spacer16"></div>
        <p>
          <FormattedMessage
            {...messages.connectDescription2}
            values={{
              deploymentsSettingRoute: (
                <strong>
                  {messages.deploymentsSettingRoute.defaultMessage}
                </strong>
              ),
              chatButtonsSettingRoute: (
                <strong>
                  {messages.chatButtonsSettingRoute.defaultMessage}
                </strong>
              )
            }}
          />
          <FormattedMessageWithLink
            url={
              useIsNewIA()
                ? links.learnMoreSalesforceDeploymentInfoNewIA
                : links.learnMoreSalesforceDeploymentInfoClassic
            }
            {...messages.connectDescription2Link}
          />
        </p>
        <div className="TabContent__spacer16"></div>
        {isServiceDeskLoading ? (
          <TextAreaSkeleton
            id={FIELDS.ORGANIZATION_DEPLOYMENT_CODE}
            className="TabContent__field TabContent__security-tab-field"
            hideLabel={false}
          />
        ) : (
          <TextArea
            {...registerInputField({
              field: FIELDS.ORGANIZATION_DEPLOYMENT_CODE,
              labelText: messages.deploymentCodeLabel,
              placeholderText: messages.deploymentCodePlaceholder,
              changeState: deploymentCodeState,
              readOnly
            })}
            id={FIELDS.ORGANIZATION_DEPLOYMENT_CODE}
            className="TabContent__field TabContent__security-tab-field"
            rows={8}
          />
        )}
        <div className="TabContent__spacer24"></div>
        {isServiceDeskLoading ? (
          <TextAreaSkeleton
            id={FIELDS.BUTTON_CODE}
            className="TabContent__field TabContent__security-tab-field"
            hideLabel={false}
          />
        ) : (
          <TextArea
            {...registerInputField({
              field: FIELDS.BUTTON_CODE,
              labelText: messages.chatButtonLabel,
              placeholderText: messages.chatButtonPlaceholder,
              changeState: buttonCodeState,
              readOnly
            })}
            id={FIELDS.BUTTON_CODE}
            className="TabContent__field TabContent__security-tab-field"
            rows={8}
          />
        )}
      </div>
      <div className="ServiceDesk__connect-button-container">
        <Button
          kind="tertiary"
          onClick={onConnectAccount}
          disabled={isConnectButtonDisabled}
          size="sm"
          id={IDS.CONNECT_ACCOUNT_BUTTON}>
          <FormattedMessage {...commonMessages.connectAccountButton} />
        </Button>
        {connectServiceDeskStatus !== ApiStatus.IDLE && (
          <div className="ServiceDesk__connect-button-status">
            <InlineLoading
              description={statusMessage}
              status={
                connectServiceDeskStatus === ApiStatus.SUCCEEDED
                  ? LoaderStates.SUCCESS
                  : connectServiceDeskStatus === ApiStatus.ERROR
                  ? LoaderStates.ERROR
                  : LoaderStates.ACTIVE
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
